import React, { Suspense, useState, useEffect } from 'react';
import LoadingSpinner from '../LoadingSpinner';
import {
  migrateAllLocalStorageToServer,
  getFichaMedicaCatalogosExtras,
} from '../../data/consultorioData';
import { Form, Dropdown } from 'react-bootstrap';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ConsultorioStorageBar from './ConsultorioStorageBar';
import ConsultorioBrand from './ConsultorioBrand';
import ConsultorioCitasNotifications from './ConsultorioCitasNotifications';
import ConsultorioCitasFloating from './ConsultorioCitasFloating';
import ConsultorioVoiceFab from './ConsultorioVoiceFab';
import { ConsultorioCitasProvider } from '../../context/ConsultorioCitasContext';
import { NuevoPacienteProvider, useNuevoPaciente } from '../../context/NuevoPacienteContext';
import { ConsultorioBrandingProvider } from '../../context/ConsultorioBrandingContext';
import '../../styles/consultorio.css';
import '../../styles/consultorio-nestify.css';

const NP_BRAND = '#2262e2';

const NAV_ITEMS = [
  { to: '/consultorio', label: 'Dashboard', icon: 'bi-grid-1x2-fill', exact: true },
  { to: '/consultorio/pacientes', label: 'Pacientes', icon: 'bi-people-fill' },
  { to: '/consultorio/citas', label: 'Calendario', icon: 'bi-calendar3' },
  { to: '/consultorio/productos', label: 'Productos', icon: 'bi-box-seam-fill' },
  { to: '/consultorio/facturas', label: 'Facturas', icon: 'bi-receipt' },
];

const ADMIN_ITEMS = [
  { to: '/consultorio/usuarios', label: 'Usuarios', icon: 'bi-person-gear' },
];

const AVATAR_COLORS = [
  ['#2262e2', '#1b56c9'], ['#4a88ef', '#2262e2'], ['#16a34a', '#15803d'],
  ['#d97706', '#b45309'], ['#7c3aed', '#6d28d9'], ['#0891b2', '#0e7490'],
];

function ConsultorioLayoutInner() {
  const location = useLocation();
  const hideGlobalStorageBar = /\/consultorio\/usuarios\/?$/.test(location.pathname);
  const { user, logout } = useAuth();
  const { openNuevoPaciente } = useNuevoPaciente();
  const isConsultorioAdmin = user?.consultorio_role === 'administrador';
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    migrateAllLocalStorageToServer().then(() => {
      getFichaMedicaCatalogosExtras(user.id);
    });
  }, [user?.id]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/consultorio/pacientes?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const isActive = (item) =>
    item.exact
      ? location.pathname === item.to
      : location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  const userInitial = (user?.nombre || 'U').charAt(0).toUpperCase();
  const charCode = userInitial.charCodeAt(0) - 65;
  const [ac1, ac2] = AVATAR_COLORS[Math.abs(charCode) % AVATAR_COLORS.length];
  const roleLabel = isConsultorioAdmin ? 'Administrador' : 'Profesional';

  const topNavItems = [
    ...NAV_ITEMS,
    ...(isConsultorioAdmin ? ADMIN_ITEMS : []),
  ];

  const renderTopNavPill = (item) => {
    const active = isActive(item);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`csl-topnav-pill ${active ? 'csl-topnav-pill--active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      >
        {item.label}
      </Link>
    );
  };

  const renderRailLink = (item) => {
    const active = isActive(item);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`csl-rail-btn ${active ? 'csl-rail-btn--active' : ''}`}
        title={item.label}
        aria-label={item.label}
        onClick={() => setMobileMenuOpen(false)}
      >
        <i className={`bi ${item.icon}`} />
      </Link>
    );
  };

  const railContent = (
    <>
      <nav className="csl-rail-nav" aria-label="Menú principal">
        {topNavItems.map(renderRailLink)}
      </nav>

      <div className="csl-rail-footer">
        <Link
          to="/consultorio/perfil"
          className={`csl-rail-btn ${location.pathname === '/consultorio/perfil' ? 'csl-rail-btn--active' : ''}`}
          title="Mi perfil"
        >
          <i className="bi bi-person-vcard" />
        </Link>
        <button type="button" className="csl-rail-btn csl-rail-btn--muted" onClick={logout} title="Cerrar sesión">
          <i className="bi bi-box-arrow-right" />
        </button>
      </div>
    </>
  );

  return (
    <ConsultorioCitasProvider>
    <div className="consultorio-wrap consultorio-wrap--nestify consultorio-wrap--dash d-flex">
      <aside className="csl-rail d-none d-md-flex flex-column">
        {railContent}
      </aside>

      {mobileMenuOpen && (
        <div
          className="csl-mobile-backdrop d-md-none"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}
      <aside className={`csl-rail csl-rail--drawer d-md-none flex-column ${mobileMenuOpen ? 'is-open' : ''}`}>
        {railContent}
      </aside>

      <div className="csl-shell flex-grow-1 d-flex flex-column min-vh-100">
        <header className="csl-topbar">
          <button
            type="button"
            className="csl-topbar-icon d-md-none"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Abrir menú"
          >
            <i className="bi bi-list" />
          </button>

          <div className="csl-topbar-leading">
            <div className="csl-topbar-brand">
              <ConsultorioBrand compact />
            </div>
            <nav className="csl-topnav" aria-label="Secciones del consultorio">
              {topNavItems.map(renderTopNavPill)}
            </nav>
          </div>

          {location.pathname.startsWith('/consultorio/paciente/') && (
            <nav className="csl-topbar-crumb d-none d-lg-flex" aria-label="Ubicación">
              <Link to="/consultorio/pacientes">Pacientes</Link>
              <i className="bi bi-chevron-right" />
              <span>Ficha</span>
            </nav>
          )}

          <div className="csl-topbar-actions">
            {searchOpen ? (
              <Form className="csl-topbar-search-form" onSubmit={handleSearch}>
                <i className="bi bi-search" />
                <input
                  type="search"
                  className="csl-topbar-search-input"
                  placeholder="Buscar paciente…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  aria-label="Buscar paciente"
                />
                <button type="button" className="csl-topbar-search-close" onClick={() => setSearchOpen(false)}>
                  <i className="bi bi-x-lg" />
                </button>
              </Form>
            ) : (
              <button
                type="button"
                className="csl-topbar-icon"
                onClick={() => setSearchOpen(true)}
                aria-label="Buscar"
              >
                <i className="bi bi-search" />
              </button>
            )}

            <ConsultorioCitasNotifications />

            <button
              type="button"
              className="csl-topbar-btn-nuevo"
              onClick={() => openNuevoPaciente()}
              aria-label="Registrar nuevo paciente"
              title="Registrar nuevo paciente"
            >
              <span className="csl-topbar-btn-nuevo__icon" aria-hidden>
                <i className="bi bi-person-plus-fill" />
              </span>
              <span className="csl-topbar-btn-nuevo__text">Nuevo paciente</span>
            </button>

            <Dropdown align="end">
              <Dropdown.Toggle variant="link" className="csl-topbar-user" id="csl-user-dd">
                <div
                  className="csl-topbar-avatar"
                  style={{ background: `linear-gradient(135deg, ${ac1} 0%, ${ac2} 100%)` }}
                >
                  {userInitial}
                </div>
                <div className="csl-topbar-user-text d-none d-xl-block">
                  <span className="csl-topbar-user-name">{user?.nombre || 'Usuario'}</span>
                  <span className="csl-topbar-user-role">{roleLabel}</span>
                </div>
                <i className="bi bi-chevron-down csl-topbar-user-chevron d-none d-sm-inline" />
              </Dropdown.Toggle>

              <Dropdown.Menu className="csl-dropdown-menu">
                <div className="csl-dd-header">
                  <div
                    className="csl-dd-avatar"
                    style={{ background: `linear-gradient(135deg, ${ac1} 0%, ${ac2} 100%)` }}
                  >
                    {userInitial}
                  </div>
                  <div>
                    <div className="csl-dd-name">{user?.nombre}</div>
                    <div className="csl-dd-email">{user?.email || roleLabel}</div>
                  </div>
                </div>
                <Dropdown.Divider />
                {topNavItems.map((item) => (
                  <Dropdown.Item key={item.to} as={Link} to={item.to} className="csl-dd-item">
                    <span className="csl-dd-item-icon" style={{ color: NP_BRAND }}>
                      <i className={`bi ${item.icon}`} />
                    </span>
                    {item.label}
                  </Dropdown.Item>
                ))}
                <Dropdown.Divider />
                <Dropdown.Item as={Link} to="/consultorio/perfil" className="csl-dd-item">
                  <span className="csl-dd-item-icon" style={{ color: NP_BRAND }}>
                    <i className="bi bi-person-vcard" />
                  </span>
                  Mi perfil profesional
                </Dropdown.Item>
                <Dropdown.Item as={Link} to="/" className="csl-dd-item">
                  <span className="csl-dd-item-icon" style={{ color: '#64748b' }}>
                    <i className="bi bi-box-arrow-up-right" />
                  </span>
                  Ir al sitio principal
                </Dropdown.Item>
                <Dropdown.Item onClick={logout} className="csl-dd-item csl-dd-item--danger">
                  <span className="csl-dd-item-icon"><i className="bi bi-box-arrow-right" /></span>
                  Cerrar sesión
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </header>

        <main
          className={`consultorio-main csl-main${user?.tenant_id && !hideGlobalStorageBar ? ' csl-main--storage-float' : ''}`}
        >
          <Suspense fallback={<LoadingSpinner />}>
            <div className="ui-page-enter">
              <Outlet />
            </div>
          </Suspense>
        </main>
      </div>
      {user?.tenant_id && !hideGlobalStorageBar ? (
        <ConsultorioStorageBar
          tenantId={user.tenant_id}
          limitMb={user.storage_limit_mb}
          memberDoctorIds={[]}
          variant="floating"
        />
      ) : null}
      <ConsultorioCitasFloating />
      <ConsultorioVoiceFab />
    </div>
    </ConsultorioCitasProvider>
  );
};

const ConsultorioLayout = () => (
  <NuevoPacienteProvider>
    <ConsultorioBrandingProvider>
      <ConsultorioLayoutInner />
    </ConsultorioBrandingProvider>
  </NuevoPacienteProvider>
);

export default ConsultorioLayout;
